import { Lightbulb, Heart, Sparkles, ArrowLeft, MessageSquarePlus, Code2, Mail, Phone, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";
import BottomNav from "../components/BottomNav";
import Logo from "../components/Logo";
import DisclaimerBanner from "../components/DisclaimerBanner";

export default function About() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen pb-24" data-testid="about-page">
      <Header title={t("about.headerTitle")} subtitle="Il Mio Vigneto" right={
        <button onClick={() => navigate(-1)} className="p-2 rounded-lg"
          style={{ border: "2px solid var(--border)" }} aria-label={t("about.backAriaLabel")} data-testid="about-back">
          <ArrowLeft size={18} />
        </button>
      } />
      <main className="max-w-2xl mx-auto px-4 py-5 space-y-5">

        {/* Hero card */}
        <section className="vs-card p-6 text-center vs-fade-up"
          style={{ background: "var(--gradient-vine)", color: "white", borderColor: "transparent" }}>
          <div className="flex justify-center mb-3"><Logo size={48} /></div>
          <h1 className="heading text-3xl font-extrabold mb-1">Il Mio Vigneto</h1>
          <p className="text-sm" style={{ color: "rgba(255,255,255,0.85)" }}>
            {t("about.heroTagline")}
          </p>
        </section>

        {/* Idea */}
        <section className="vs-card p-5 vs-fade-up" data-testid="idea-card" style={{ animationDelay: "80ms" }}>
          <div className="flex items-start gap-3 mb-3">
            <div className="shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: "var(--gold)", border: "2px solid #7A4B00" }}>
              <Lightbulb size={22} color="#7A4B00" strokeWidth={2.4} />
            </div>
            <div>
              <h2 className="heading text-xl font-extrabold">{t("about.ideaTitle")}</h2>
              <p className="text-sm" style={{ color: "var(--text-muted)" }}>
                {t("about.ideaSubtitle")}
              </p>
            </div>
          </div>
          <div className="p-4 rounded-xl"
            style={{ background: "var(--secondary)", border: "2px solid var(--border)" }}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-full flex items-center justify-center font-extrabold text-lg"
                style={{ background: "var(--primary)", color: "white" }}>
                LS
              </div>
              <div>
                <div className="heading text-lg font-bold" data-testid="about-name">{t("about.founderName")}</div>
                <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--grape)" }}>
                  {t("about.founderRole")}
                </div>
              </div>
            </div>
            <p className="text-sm mb-3" style={{ color: "var(--text-secondary)" }}>
              {t("about.founderBio")}
            </p>
            <a
              href="/suggerimenti"
              onClick={(e) => { e.preventDefault(); navigate("/suggerimenti"); }}
              className="vs-btn-secondary w-full inline-flex items-center justify-center gap-2 text-sm"
              data-testid="about-send-suggestion"
            >
              <MessageSquarePlus size={16} /> {t("about.sendSuggestionButton")}
            </a>
          </div>
        </section>

        {/* Tech credits */}
        <section className="vs-card p-5 vs-fade-up" style={{ animationDelay: "160ms" }}>
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={20} style={{ color: "var(--accent)" }} />
            <h3 className="heading text-lg font-bold">{t("about.howItWorksTitle")}</h3>
          </div>
          <ul className="text-sm space-y-2" style={{ color: "var(--text-secondary)" }}>
            <li className="flex items-start gap-2">
              <span className="vs-grape-dot mt-2" />
              <span><strong>{t("about.featureWeatherLabel")}</strong> {t("about.featureWeatherText")}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="vs-grape-dot mt-2" />
              <span><strong>{t("about.featureDiagnosisLabel")}</strong> {t("about.featureDiagnosisText")}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="vs-grape-dot mt-2" />
              <span><strong>{t("about.featureFertilizerLabel")}</strong> {t("about.featureFertilizerText")}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="vs-grape-dot mt-2" />
              <span><strong>{t("about.featureJournalLabel")}</strong> {t("about.featureJournalText")}</span>
            </li>
          </ul>
        </section>

        {/* Contatti sviluppatore */}
        <section className="vs-card p-5 vs-fade-up" style={{ animationDelay: "200ms" }}>
          <div className="flex items-start gap-3 mb-4">
            <div className="shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, #1a3a0a, #4a8a2a)" }}>
              <Code2 size={22} color="white" strokeWidth={2.4} />
            </div>
            <div>
              <h2 className="heading text-xl font-extrabold">{t("about.contactTitle")}</h2>
              <p className="text-sm" style={{ color: "var(--text-muted)" }}>
                {t("about.contactSubtitle")}
              </p>
            </div>
          </div>

          <div className="p-4 rounded-xl mb-4"
            style={{ background: "var(--secondary)", border: "2px solid var(--border)" }}>
            <p className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>
              {t("about.contactBody")}
            </p>

            <div className="space-y-3">
              <a href="mailto:luciano0871l.s@gmail.com"
                className="flex items-center gap-3 p-3 rounded-xl transition-opacity hover:opacity-80"
                style={{ background: "white", border: "1.5px solid var(--border)" }}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: "linear-gradient(135deg, #1a3a0a, #4a8a2a)" }}>
                  <Mail size={16} color="white" />
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{t("about.emailLabel")}</div>
                  <div className="text-sm font-bold" style={{ color: "var(--primary)" }}>luciano0871l.s@gmail.com</div>
                </div>
                <ExternalLink size={14} style={{ color: "var(--text-muted)", marginLeft: "auto" }} />
              </a>

              <a href="https://wa.me/393385843051"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-xl transition-opacity hover:opacity-80"
                style={{ background: "white", border: "1.5px solid var(--border)" }}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: "#25D366" }}>
                  <Phone size={16} color="white" />
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{t("about.whatsappLabel")}</div>
                  <div className="text-sm font-bold" style={{ color: "var(--primary)" }}>+39 338 584 3051</div>
                </div>
                <ExternalLink size={14} style={{ color: "var(--text-muted)", marginLeft: "auto" }} />
              </a>
            </div>
          </div>
        </section>

        {/* Disclaimer */}
        <DisclaimerBanner variant="full" />

        {/* Version */}
        <section className="vs-card p-4 text-center vs-fade-up" style={{ animationDelay: "240ms" }}>
          <div className="flex items-center justify-center gap-2 text-xs mb-1" style={{ color: "var(--text-muted)" }}>
            <Heart size={14} style={{ color: "var(--accent)" }} /> {t("about.footerTagline")}
          </div>
          <div className="text-xs font-semibold" style={{ color: "var(--text-muted)" }}>
            {t("about.footerVersion")}
          </div>
        </section>
      </main>
      <BottomNav />
    </div>
  );
}
